import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Package, Mail, Inbox, RefreshCw } from "lucide-react";
import { useSEO } from "@/lib/seo";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";

const fmt = (iso) => { try { return new Date(iso).toLocaleString("en-CA", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };

export default function Admin() {
  useSEO("Admin Dashboard", "");
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState({ quotes: 0, new_quotes: 0, contacts: 0 });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [q, c, s] = await Promise.all([api.get("/admin/quotes"), api.get("/admin/contacts"), api.get("/admin/stats")]);
      setQuotes(q.data); setContacts(c.data); setStats(s.data);
    } catch { /* handled by redirect */ } finally { setLoading(false); }
  };

  useEffect(() => {
    if (user === false) nav("/admin/login");
    if (user && user.email) load();
  }, [user]);

  if (user === null || (user && loading)) return <div className="min-h-[70vh] flex items-center justify-center font-head uppercase tracking-widest text-[#94A3B8]" data-testid="admin-loading">Loading dashboard…</div>;
  if (!user) return null;

  const Stat = ({ icon: Icon, label, value, testId }) => (
    <div className="bg-white border-2 border-[#0B192C] p-6" data-testid={testId}>
      <div className="flex items-center justify-between"><Icon className="text-[#FF5A00]" /><span className="font-head font-black text-4xl">{value}</span></div>
      <div className="font-head uppercase tracking-wide text-xs font-700 text-[#94A3B8] mt-3">{label}</div>
    </div>
  );

  return (
    <div className="bg-[#F8FAFC] min-h-screen py-12" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="font-head uppercase tracking-[0.3em] text-[#FF5A00] text-sm font-700">Cargowyn Admin</div>
            <h1 className="font-head font-black uppercase text-4xl tracking-tighter">Dashboard</h1>
          </div>
          <div className="flex gap-3">
            <button onClick={load} data-testid="admin-refresh" className="flex items-center gap-2 border-2 border-[#0B192C] px-5 py-3 font-head font-700 uppercase text-sm tracking-wide hover:bg-[#0B192C] hover:text-white transition-colors"><RefreshCw size={16} /> Refresh</button>
            <button onClick={() => { logout(); nav("/admin/login"); }} data-testid="admin-logout" className="flex items-center gap-2 bg-[#FF5A00] text-white px-5 py-3 font-head font-700 uppercase text-sm tracking-wide hover:bg-[#E65100] transition-colors"><LogOut size={16} /> Logout</button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
          <Stat icon={Package} label="Total Quote Requests" value={stats.quotes} testId="stat-quotes" />
          <Stat icon={Inbox} label="New / Unhandled" value={stats.new_quotes} testId="stat-new" />
          <Stat icon={Mail} label="Contact Messages" value={stats.contacts} testId="stat-contacts" />
        </div>

        <Tabs defaultValue="quotes">
          <TabsList className="rounded-none bg-white border-2 border-[#0B192C] p-0 h-auto">
            <TabsTrigger value="quotes" data-testid="tab-quotes" className="rounded-none font-head uppercase tracking-wide data-[state=active]:bg-[#0B192C] data-[state=active]:text-white px-6 py-3">Quotes ({quotes.length})</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts" className="rounded-none font-head uppercase tracking-wide data-[state=active]:bg-[#0B192C] data-[state=active]:text-white px-6 py-3">Messages ({contacts.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="quotes" className="mt-6">
            {quotes.length === 0 ? <Empty label="No quote requests yet." /> : (
              <div className="space-y-4">
                {quotes.map((q) => (
                  <div key={q.id} className="bg-white border-2 border-[#0B192C]/10 p-6" data-testid={`quote-row-${q.id}`}>
                    <div className="flex items-start justify-between flex-wrap gap-3">
                      <div>
                        <div className="font-head font-800 uppercase text-xl">{q.name} {q.company && <span className="text-[#94A3B8]">· {q.company}</span>}</div>
                        <div className="text-[#475569] text-sm mt-1">{q.email} · {q.phone}</div>
                      </div>
                      <Badge className="rounded-none bg-[#FF5A00] font-head uppercase tracking-wide">{q.freight_type}</Badge>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4 text-sm">
                      <Info label="Route" value={`${q.origin} → ${q.destination}`} />
                      <Info label="Weight" value={q.weight || "—"} />
                      <Info label="Dimensions" value={q.dimensions || "—"} />
                      <Info label="Ready" value={q.ready_date || "—"} />
                    </div>
                    {q.details && <p className="mt-4 text-[#334155] bg-[#F8FAFC] border-l-4 border-[#FF5A00] p-3 text-sm">{q.details}</p>}
                    <div className="text-[#94A3B8] text-xs mt-4 font-head uppercase tracking-wide">Received {fmt(q.created_at)}</div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="contacts" className="mt-6">
            {contacts.length === 0 ? <Empty label="No messages yet." /> : (
              <div className="space-y-4">
                {contacts.map((c) => (
                  <div key={c.id} className="bg-white border-2 border-[#0B192C]/10 p-6" data-testid={`contact-row-${c.id}`}>
                    <div className="flex items-start justify-between flex-wrap gap-3">
                      <div><div className="font-head font-800 uppercase text-xl">{c.name}</div><div className="text-[#475569] text-sm mt-1">{c.email} {c.phone && `· ${c.phone}`}</div></div>
                      {c.subject && <Badge className="rounded-none bg-[#0B192C] font-head uppercase tracking-wide">{c.subject}</Badge>}
                    </div>
                    <p className="mt-4 text-[#334155] bg-[#F8FAFC] border-l-4 border-[#FF5A00] p-3">{c.message}</p>
                    <div className="text-[#94A3B8] text-xs mt-4 font-head uppercase tracking-wide">Received {fmt(c.created_at)}</div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

const Info = ({ label, value }) => (<div><div className="font-head uppercase tracking-wide text-[10px] font-700 text-[#94A3B8]">{label}</div><div className="text-[#0B192C] font-600">{value}</div></div>);
const Empty = ({ label }) => (<div className="bg-white border-2 border-dashed border-[#0B192C]/20 p-16 text-center font-head uppercase tracking-wide text-[#94A3B8]" data-testid="admin-empty">{label}</div>);
